import React, { FC, useEffect, useMemo, useState } from "react";
import "./packages.scss";
import CommonHeader from "../../shared/common-header/common-header";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { coursesActions, fetchCourses, fetchCoursesStatus } from "../../reducers/coursesSlice";
import { clubsActions, fetchClubs } from "../../reducers/clubsSlice";
import { CoursesPayload } from "../../models/courses";
import { ClubPayload } from "../../models/clubs";
import { SAGA_STATUS_SUCCESS } from "../../utils/constants";

const buildEmbedSnippet = (url: string) =>
  `<iframe src="${url}" width="100%" height="900" frameborder="0" allowtransparency="true" style="border:none;max-width:1200px;display:block;margin:0 auto;background:transparent;"></iframe>`;

type CourseOption = CoursesPayload & { courseName: string };

const Packages: FC = () => {
  const dispatch = useAppDispatch();
  const courses: CoursesPayload[] = useAppSelector(fetchCourses);
  const clubs: ClubPayload[] = useAppSelector(fetchClubs);
  const coursesListedStatus = useAppSelector(fetchCoursesStatus);

  const [selectedCourseId, setSelectedCourseId] = useState<string>("");
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [copiedEmbed, setCopiedEmbed] = useState(false);

  useEffect(() => {
    dispatch(clubsActions.clubsList());
    dispatch(coursesActions.coursesList({ isDeleted: false }));
  }, [dispatch]);

  const courseOptions: CourseOption[] = useMemo(() => {
    return courses
      .map((course) => {
        const club = clubs.find(({ _id }) => _id === course.clubObjectId);
        if (!club) return null;
        return {
          ...course,
          courseName: club.name ?? "Course",
        };
      })
      .filter(Boolean) as CourseOption[];
  }, [courses, clubs]);

  useEffect(() => {
    if (coursesListedStatus !== SAGA_STATUS_SUCCESS || !courseOptions.length) {
      return;
    }
    if (!selectedCourseId || !courseOptions.some((c) => c._id === selectedCourseId)) {
      setSelectedCourseId(courseOptions[0]._id ?? "");
    }
  }, [coursesListedStatus, courseOptions, selectedCourseId]);

  const selectedCourse = courseOptions.find((c) => c._id === selectedCourseId);
  const embedUrl = selectedCourse?.stayPlayPackagesEmbedUrl?.trim() ?? "";

  const handleCopy = async (text: string, type: "url" | "embed") => {
    try {
      await navigator.clipboard.writeText(text);
      if (type === "url") {
        setCopiedUrl(true);
        setTimeout(() => setCopiedUrl(false), 2000);
      } else {
        setCopiedEmbed(true);
        setTimeout(() => setCopiedEmbed(false), 2000);
      }
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Failed to copy");
    }
  };

  return (
    <div className="packagesWrapper">
      <CommonHeader sectionTitle="Packages" />
      <div className="container">
        {courseOptions.length > 1 && (
          <div className="packagesCourseSelect">
            <label htmlFor="packages-course-select">Course</label>
            <select
              id="packages-course-select"
              className="form-select"
              value={selectedCourseId}
              onChange={(e) => setSelectedCourseId(e.target.value)}
            >
              {courseOptions.map((course) => (
                <option key={course._id} value={course._id}>
                  {course.courseName}
                </option>
              ))}
            </select>
          </div>
        )}

        {!embedUrl ? (
          <div className="packagesEmbedCard packagesEmbedCard--empty">
            <p className="mb-0">
              {selectedCourse
                ? `No Stay & Play packages link has been configured for ${selectedCourse.courseName} yet.`
                : "No courses available."}
            </p>
          </div>
        ) : (
          <div className="packagesEmbedCard">
            <div className="packagesEmbedCard__header">
              <div className="packagesEmbedCard__url">
                <label htmlFor="packages-embed-url">Embed URL</label>
                <input
                  id="packages-embed-url"
                  type="text"
                  readOnly
                  value={embedUrl}
                  onFocus={(e) => e.target.select()}
                />
              </div>
              <div className="packagesEmbedCard__actions">
                <button
                  type="button"
                  className="button button-primary button-rounded fw600"
                  onClick={() => handleCopy(embedUrl, "url")}
                >
                  {copiedUrl ? "Copied!" : "Copy URL"}
                </button>
                <button
                  type="button"
                  className="button button-secondary button-rounded fw600"
                  onClick={() => handleCopy(buildEmbedSnippet(embedUrl), "embed")}
                >
                  {copiedEmbed ? "Copied!" : "Copy Embed Code"}
                </button>
              </div>
            </div>

            <div className="packagesEmbedCard__preview">
              <iframe
                title="Packages"
                src={embedUrl}
                width="100%"
                height="900"
                frameBorder="0"
                style={{
                  border: "none",
                  maxWidth: 1200,
                  display: "block",
                  margin: "0 auto",
                  background: "transparent",
                }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Packages;
